import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/apply-leaves"; // Updated to match backend route

// Fetch Leaves (All or by teacherEmail/adminEmail)
export const fetchLeaves = createAsyncThunk(
  "leaves/fetch",
  async ({ teacherEmail, adminEmail } = {}, { rejectWithValue }) => {
    try {
      let response;
      if (teacherEmail) {
        response = await hrmsGetRequest(`${API_URL}/email/${teacherEmail}`);
      } else if (adminEmail) {
        response = await hrmsGetRequest(`${API_URL}/email/${adminEmail}`);
      } else {
        response = await hrmsGetRequest(API_URL);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Apply for Leave
export const applyLeave = createAsyncThunk(
  "leaves/apply",
  async (leaveData, { rejectWithValue }) => {
    try {
      // Ensure the data structure matches backend expectations
      const formattedData = {
        teacherEmail: leaveData.teacherEmail || leaveData.adminEmail,
        leaveType: leaveData.leaveType,
        dateFrom: leaveData.dateFrom,
        dateTo: leaveData.dateTo || null,
        reason: leaveData.reason || "",
        status: "Pending" // Default status
      };

      const response = await hrmsPostRequest(API_URL, formattedData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update Leave
export const updateLeave = createAsyncThunk(
  "leaves/update",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete Leave
export const deleteLeave = createAsyncThunk(
  "leaves/delete",
  async (id, { rejectWithValue }) => {
    try {
      await hrmsDeleteRequest(`${API_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const applyLeaveSlice = createSlice({
  name: "leaves",
  initialState: {
    leaves: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearLeaves: (state) => {
      state.leaves = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leaves
      .addCase(fetchLeaves.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchLeaves.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.leaves = action.payload;
        state.error = null;
      })
      .addCase(fetchLeaves.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch leaves";
      })
      // Apply Leave
      .addCase(applyLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(applyLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.leaves.push(action.payload);
        state.error = null;
      })
      .addCase(applyLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to apply leave";
      })
      // Update Leave
      .addCase(updateLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.leaves.findIndex((leave) => leave.id === action.payload.id);
        if (index !== -1) {
          state.leaves[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to update leave";
      })
      // Delete Leave
      .addCase(deleteLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.leaves = state.leaves.filter((leave) => leave.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to delete leave";
      });
  },
});

export const { clearLeaves } = applyLeaveSlice.actions;

export default applyLeaveSlice.reducer;